<template>
    <!-- TODO: add support for container query -->
    <div class="@container w-full">
        <!-- Tab buttons -->
        <div class="flex h-24 @max-lg:h-24 sm:h-18 justify-stretch w-full" v-if="tabs.length > 1">
            <button
                v-for="(tab, index) in tabs"
                :key="index"
                :class="[
                    'group min-w-0 relative @max-lg:p-0 sm:p-4 border-b transition-colors duration-300 flex-1 text-base @max-lg:text-base sm:text-lg font-semibold @max-lg:font-semibold sm:font-medium',
                    currentTab === index ? 'text-default' : 'border-surface-border text-sub hover:text-default cursor-pointer hover:border-gray-500'
                ]"
                @click="selectTab(index)"
            >
                <div class="flex flex-col @max-lg:flex-col sm:flex-row items-center justify-center gap-2 truncate min-w-0">
                    <template v-if="tab?.units">
                        <UnitBadge v-if="currentTab === index" :units="tab.units" variant="dark" />
                        <UnitBadge v-else :units="tab.units" variant="outline" class="text-sub group-hover:text-default" />
                    </template>
                    <div class="truncate min-w-0 w-full sm:w-auto">{{ tab?.title }}</div>
                </div>
                <div class="h-0 bg-system-dark w-full transition-all absolute bottom-0 left-0" :class="{ 'h-0.5': currentTab === index }"></div>
            </button>
        </div>

        <!-- Tab content with transition -->
        <transition
            enter-active-class="transition-opacity duration-300 ease-in"
            leave-active-class="transition-opacity duration-300 ease-out"
            enter-from-class="opacity-0"
            leave-to-class="opacity-0"
            mode="out-in"
        >
            <div :key="currentTab" class="text-default">
                <slot :name="tabTypes[currentTab]" />
            </div>
        </transition>
    </div>
</template>

<script setup lang="ts">
    import { ref, watch, useSlots, computed } from 'vue'
    import UnitBadge from '../elements/UnitBadge.vue'
    import { UnitType } from '../../types/units.js'

    // Define allowed slot names
    type AllowedSlots = 'temperature' | 'humidity'

    // Type the slots
    const slots = useSlots() as Record<AllowedSlots, any>
    const tabTypes = computed(() => Object.keys(slots) as AllowedSlots[])

    const tabs = computed(() =>
        tabTypes.value.map((type: AllowedSlots) => {
            const defaultTab = {
                title: type
            }
            const tabTypeOptions: Record<AllowedSlots, { title: string; units?: UnitType[] }> = {
                temperature: {
                    title: 'Temperature',
                    units: ['temperature']
                },
                humidity: {
                    title: 'Humidity',
                    units: ['humidity']
                }
            }
            return tabTypeOptions[type] ?? defaultTab
        })
    )

    const props = withDefaults(
        defineProps<{
            initialTab?: number
        }>(),
        {
            initialTab: 0
        }
    )

    const emit = defineEmits<{
        (e: 'tabChange', index: number): void
    }>()

    const currentTab = ref(props.initialTab)

    // Watch for initialTab changes
    watch(
        () => props.initialTab,
        (newValue) => {
            currentTab.value = newValue
        }
    )

    const selectTab = (index: number) => {
        currentTab.value = index
        emit('tabChange', index)
    }
</script>
